import { normalizeSpecialistRoleKey } from './activeSpecialistRoleHelper';
import { CASE_PHASE, isDriDocumentPhase, resolveCasePhase } from './flowCasePhaseHelper';
import { resolveImportStageFromState } from './importStageHelper';

const EMPTY_NEW_EDIT = {
  arrayListPrincipal: [],
  arrayListSecondary: [],
  arrayListPrincipalEnabled: false,
  arrayListSecondaryEnabled: false
};

const MODULE_LIST_KEYS = {
  technology: {
    all: 'listFieldsTechnology',
    neu: 'listFieldsTechnologyNew',
    pre: 'listFieldsTechnologyPre'
  },
  alternative: {
    all: 'listFieldsAlternative',
    neu: 'listFieldsAlternativeNew',
    pre: 'listFieldsAlternativePre'
  },
  experience: {
    all: 'listFieldsExperience',
    neu: 'listFieldsExperienceNew',
    pre: 'listFieldsExperiencePre'
  },
  innovation: {
    all: 'listFieldsInnovation',
    neu: 'listFieldsInnovationNew',
    pre: 'listFieldsInnovationPre'
  },
  assistance: {
    all: 'listFieldsAssistance',
    neu: 'listFieldsAssistanceNew',
    pre: 'listFieldsAssistancePre'
  },
  financial: {
    all: 'listFieldsFinancial',
    neu: 'listFieldsFinancialNew',
    pre: 'listFieldsFinancialPre'
  },
  vision: {
    all: 'listFieldsVision',
    neu: 'listFieldsVisionNew',
    pre: 'listFieldsVisionPre'
  },
  regulation: {
    all: 'listFieldsRegulation',
    neu: 'listFieldsRegulationNew',
    pre: 'listFieldsRegulationPre'
  }
};

export const isNewEditPreManagerFlag = (field) => {
  const raw = field?.new_edit_premanager;
  return raw === true || String(raw).toLowerCase() === 'true';
};

const nsoValue = (item) => {
  const raw = item?.new_section_order;
  if (raw === undefined || raw === null || raw === '' || String(raw).toLowerCase() === 'null') {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const nsfoValue = (item) => {
  const raw = item?.new_section_field_order;
  if (raw === undefined || raw === null || raw === '' || String(raw).toLowerCase() === 'null') {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const hydratePreMultimediaComponent = (copy, moduleFields) => {
  if (copy.component_new || copy.field !== 'catalogmultimedia_l') {
    return copy;
  }
  const listField = moduleFields.find((item) => item.field === 'catalogmultimedia_list');
  if (!listField) {
    return copy;
  }
  return {
    ...copy,
    component_new: listField.component_new,
    key_relation: listField.key_relation || copy.key_relation,
    data_name: listField.data_name || copy.data_name,
    new_section_order: nsoValue(copy) ?? nsoValue(listField) ?? 1,
    new_section_field_order: nsfoValue(copy) ?? nsfoValue(listField) ?? 2
  };
};

const PRE_ALWAYS_FIELDS = new Set(['dri', 'dri_registered_at']);

const isPreNewEditField = (item) =>
  isNewEditPreManagerFlag(item) || PRE_ALWAYS_FIELDS.has(item?.field);

export const groupPreNewEditFields = (arrayList = []) => {
  const orders = [...new Set((arrayList || [])
    .map((element) => element.new_section_order)
    .filter((order) => order !== undefined && order !== null))]
    .sort((a, b) => Number(a) - Number(b));
  const principalOrder = orders[0];
  const secondaryOrders = new Set(orders.slice(1));

  let arrayListPrincipal = [];
  let arrayListSecondary = [];

  (arrayList || []).forEach((element) => {
    if (principalOrder === undefined || element.new_section_order === principalOrder) {
      arrayListPrincipal.push(element);
    } else if (secondaryOrders.has(element.new_section_order)) {
      arrayListSecondary.push(element);
    } else if (element.new_section_order == null && secondaryOrders.size > 0) {
      arrayListSecondary.push(element);
    } else {
      arrayListPrincipal.push(element);
    }
  });

  const byFieldOrder = (list) =>
    [...list].sort((a, b) => Number(a.new_section_field_order) - Number(b.new_section_field_order));

  arrayListPrincipal = byFieldOrder(arrayListPrincipal);
  arrayListSecondary = byFieldOrder(arrayListSecondary);

  const isVisiblePreOrNew = (element) => element.show_new || isNewEditPreManagerFlag(element) || PRE_ALWAYS_FIELDS.has(element?.field);

  return {
    arrayListPrincipal,
    arrayListSecondary,
    arrayListPrincipalEnabled: arrayListPrincipal.some(isVisiblePreOrNew),
    arrayListSecondaryEnabled: arrayListSecondary.some(isVisiblePreOrNew)
  };
};

export const collectPreNewEditFields = (moduleFields = []) => {
  const flagged = moduleFields.filter((item) => isPreNewEditField(item));
  if (!flagged.length) {
    return [];
  }

  const biblio = moduleFields.find((item) => item.field === 'bibliographic_reference');
  const biblioNsfo = nsfoValue(biblio);
  const sectionOrders = new Set(
    flagged
      .map((item) => nsoValue(item))
      .filter((order) => order !== null)
  );
  if (flagged.some((item) => nsoValue(item) === null)) {
    sectionOrders.add(2);
  }

  const byId = new Map();
  flagged.forEach((item) => {
    let copy = { ...item };
    const currentNso = nsoValue(copy);
    if (currentNso === null) {
      copy.new_section_order = 2;
    }
    if (nsfoValue(copy) === null) {
      if (copy.field === 'dri' && biblioNsfo !== null) {
        copy.new_section_field_order = biblioNsfo - 1;
      } else if (copy.field === 'dri_registered_at') {
        copy.new_section_field_order = 997;
      } else {
        copy.new_section_field_order = 998;
      }
    }
    copy = hydratePreMultimediaComponent(copy, moduleFields);
    byId.set(item.id ?? `${item.module_text}:${item.field}`, copy);
  });

  moduleFields.forEach((item) => {
    if (item.type !== 'section') {
      return;
    }
    const order = nsoValue(item);
    if (order === null || !sectionOrders.has(order) || isNewEditPreManagerFlag(item)) {
      return;
    }
    const key = item.id ?? `${item.module_text}:${item.field}`;
    if (!byId.has(key)) {
      byId.set(key, { ...item });
    }
  });

  return Array.from(byId.values());
};

const hasMagicFillApplied = (record = {}) => {
  const status = String(record?.ai_fill_status || '').toLowerCase();
  return status === 'proposed' || status === 'accepted';
};

/**
 * DRI 1 / DRI 2 / DRI0 → campos Pre (`new_edit_premanager`).
 * Prospecto o varita (llenado mágico) → ficha completa (`show_new`).
 * Alta sin id: solo el rol Pre usa el esquema corto.
 */
export const shouldUsePreDriFieldLists = ({
  userRole,
  record,
  process,
  forcePre,
  forceFull
} = {}) => {
  if (forceFull) {
    return false;
  }
  if (forcePre) {
    return true;
  }

  const selected = userRole?.roleLogin?.roleSpecialistSelected;
  const roleKey = selected?.roleKey || normalizeSpecialistRoleKey(selected);
  const hasPersistedRecord = Boolean(record?.id);

  if (!hasPersistedRecord) {
    return resolveImportStageFromState({ userRole }) === 'pre';
  }

  if (hasMagicFillApplied(record)) {
    return false;
  }

  const phase = resolveCasePhase(record, {
    roleKey,
    process: process || record.process
  });

  if (phase === CASE_PHASE.PROSPECTO) {
    return false;
  }

  if (isDriDocumentPhase(phase)) {
    return true;
  }

  return resolveImportStageFromState({ userRole }) === 'pre';
};

export const resolveNewEditVisibilityFlag = (options = {}) => (
  shouldUsePreDriFieldLists(options) ? 'new_edit_premanager' : 'show_new'
);

export const pickModuleNewEditLists = (moduleConfig = {}, moduleKey, isPreManager) => {
  const keys = MODULE_LIST_KEYS[moduleKey];
  if (!keys) {
    return { ...EMPTY_NEW_EDIT, validationFields: [] };
  }
  const rawFields = moduleConfig[keys.all] || [];
  const grouped = isPreManager
    ? groupPreNewEditFields(collectPreNewEditFields(rawFields))
    : (moduleConfig[keys.neu] || EMPTY_NEW_EDIT);
  const preValidationTypes = new Set(['field', 'relation', 'list', 'table', 'ids']);
  const validationFields = isPreManager
    ? [...(grouped.arrayListPrincipal || []), ...(grouped.arrayListSecondary || [])]
      .filter((item) => preValidationTypes.has(item.type))
    : rawFields;
  return {
    arrayListPrincipal: grouped.arrayListPrincipal || [],
    arrayListSecondary: grouped.arrayListSecondary || [],
    arrayListPrincipalEnabled: grouped.arrayListPrincipalEnabled,
    arrayListSecondaryEnabled: grouped.arrayListSecondaryEnabled,
    validationFields
  };
};
